import Paper from "@material-ui/core/Paper";
import Radio from "@material-ui/core/Radio";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import MyTypography from "@saleor/components/MyTypography";
import React, { useState } from "react";

const useStyles = makeStyles(
  theme =>
    createStyles({
      root: {
        width: "100%",
        padding: "0px"
      },
      paper: {
        width: "100% !important",
        padding: "0 !important",
        maxWidth: "100% !important",
        marginBottom: theme.spacing(2),
        borderRadius: "0 !important",
        boxShadow: "none"
      },
      table: {
        minWidth: "100%",
        maxWidth: "100%",
        boxShadow: "none"
      },
      tabHeadBg: {
        background: "#ffffff !important",
        borderBottom: "1px solid #efefef",
        padding: "0px !important"
      },

      tabBodyPad: {
        padding: "0px !important",
        "&:first-child": {
          width: "50px !important"
        }
      },
      radioCell: {
        width: "50px !important",
        background: "#ffffff !important",
        borderBottom: "1px solid #efefef",
        padding: "0px !important"
      },

      sortIcon: {
        marginLeft: "-40px",
        color: "#009FB8",
        "& svg": {
          width: "16px",
          height: "16px",
          color: "#009FB8 !important"
        }
      },
      sortLabel: {
        color: "#009FB8",
        width: "16px",
        height: "16px"
      }
    }),
  { name: "DataSortTableStyles" }
);

interface DataTableProps {
  headCells: Array<{ field: string; headerName: string; width: number }>;
  rows: Array<{ [key: string]: any }>;
  onRowSelect: (selectedRowIndex: string) => void;
  sortField: string;
  boldFields?: string[];
  selectedRow: string;
  setSelectedRow: (v) => void;
}

const DataTable: React.FC<DataTableProps> = ({
  headCells,
  rows,
  selectedRow,
  setSelectedRow,
  onRowSelect,
  sortField,
  boldFields = []
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState(sortField);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const handleChangePage = newPage => setPage(newPage);

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleRowSelect = (selected: string) => {
    setSelectedRow(selected);
    onRowSelect(selected);
  };
  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" className={classes.radioCell} />
                {headCells.map(headCell => (
                  <TableCell
                    className={classes.tabHeadBg}
                    key={headCell.field}
                    align="left"
                    padding="default"
                  >
                    {headCell.field === sortField ? (
                      <TableSortLabel
                        className={classes.sortIcon}
                        active={orderBy === headCell.field}
                        direction={orderBy === headCell.field ? order : "asc"}
                        onClick={() => handleSortRequest(headCell.field)}
                      >
                        {headCell.headerName}
                      </TableSortLabel>
                    ) : (
                      <MyTypography variant="subtitle3">
                        {headCell.headerName}
                      </MyTypography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .sort((a, b) => {
                  const isAsc = order === "asc";
                  const comparison = isAsc
                    ? a[orderBy] > b[orderBy]
                      ? 1
                      : -1
                    : b[orderBy] > a[orderBy]
                    ? 1
                    : -1;
                  return comparison;
                })
                .map((row, index) => (
                  <TableRow
                    id={row?.[sortField]}
                    data-testid={row?.[sortField]}
                    hover
                    tabIndex={-1}
                    key={index}
                    onClick={() => handleRowSelect(row?.[sortField])}
                  >
                    <TableCell
                      padding="checkbox"
                      className={classes.tabBodyPad}
                    >
                      <Radio checked={selectedRow === row?.[sortField]} />
                    </TableCell>
                    {headCells.map(cell => (
                      <TableCell
                        key={cell.field}
                        component="th"
                        scope="row"
                        padding="default"
                        style={{
                          fontWeight: boldFields.includes(cell.field)
                            ? "bold"
                            : "normal"
                        }}
                      >
                        {row[cell.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          style={{ display: "none" }}
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export default DataTable;
